/**
 * ListMembersSheet Component
 *
 * Bottom sheet showing members of a shared list.
 * Owner can remove members. Includes an "Invite friend" button.
 */

import { listMembersQueryOptions } from '@/api/queries/lists';
import { LoadingState } from '@/components/feedback/loading-state';
import { Modal } from '@/components/ui/modal';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useRemoveListMember } from '../hooks/use-list-member-mutations';
import { InviteFriendSheet } from './invite-friend-sheet';

interface ListMembersSheetProps {
  listId: string;
  isOwner: boolean;
  visible: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  editor: 'Editor',
  viewer: 'Viewer',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
};

export function ListMembersSheet({ listId, isOwner, visible, onClose }: ListMembersSheetProps) {
  const [inviteSheetVisible, setInviteSheetVisible] = useState(false);

  const { data: members, isLoading } = useQuery({
    ...listMembersQueryOptions(listId),
    enabled: visible && !!listId,
    refetchInterval: 30_000,
  });

  const removeMember = useRemoveListMember();

  const handleRemoveMember = (memberId: string) => {
    removeMember.mutate({ listId, memberId });
  };

  return (
    <>
      <Modal visible={visible} onClose={onClose}>
        <View className="bg-background rounded-xl" style={{ maxHeight: 500 }}>
          <Modal.Header>
            <View className="flex-row items-center justify-between">
              <Text className="text-lg font-bold text-foreground">
                Members
              </Text>
              <Pressable
                onPress={onClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Ionicons name="close" size={24} className="text-muted-foreground" />
              </Pressable>
            </View>
          </Modal.Header>

          {isLoading ? (
            <LoadingState message="Loading members..." fullScreen={false} />
          ) : (
            <ScrollView
              style={{ maxHeight: 360 }}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ padding: 24 }}
            >
              {(!members || members.length === 0) ? (
                <View className="items-center py-8">
                  <Text className="text-muted-foreground">
                    No members yet. Invite friends to collaborate.
                  </Text>
                </View>
              ) : (
                members.map((member) => {
                  const avatarUrl = member.userImage
                    ? imageUrl(member.userImage, 'thumbnail')
                    : null;
                  const isRemoving =
                    removeMember.isPending && removeMember.variables?.memberId === member.id;

                  return (
                    <View
                      key={member.id}
                      className="flex-row items-center py-3 border-b border-border"
                    >
                      {/* Avatar */}
                      {avatarUrl ? (
                        <Image
                          source={{ uri: avatarUrl }}
                          className="w-10 h-10 rounded-full"
                          accessibilityLabel={`${member.userName || 'Member'}'s profile photo`}
                        />
                      ) : (
                        <View className="w-10 h-10 rounded-full items-center justify-center bg-surface-secondary">
                          <Ionicons name="person" size={20} className="text-muted-foreground" />
                        </View>
                      )}

                      {/* Info */}
                      <View className="flex-1 ml-3">
                        <Text className="font-medium text-foreground" numberOfLines={1}>
                          {member.userName || 'User'}
                        </Text>
                        <View className="flex-row items-center" style={{ gap: 6 }}>
                          <Text className="text-xs text-muted-foreground">
                            {ROLE_LABELS[member.role] ?? member.role}
                          </Text>
                          {member.status === 'pending' && (
                            <View className="px-1.5 py-0.5 rounded bg-warning/10">
                              <Text className="text-xs text-warning">
                                {STATUS_LABELS[member.status]}
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>

                      {/* Remove button (owner only) */}
                      {isOwner && (
                        <Pressable
                          onPress={() => handleRemoveMember(member.id)}
                          disabled={isRemoving}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                          accessibilityRole="button"
                          accessibilityLabel={`Remove ${member.userName || 'member'}`}
                          style={{ opacity: isRemoving ? 0.5 : 1 }}
                        >
                          <Ionicons name="close-circle-outline" size={20} className="text-destructive" />
                        </Pressable>
                      )}
                    </View>
                  );
                })
              )}

              {/* Invite friend button */}
              {isOwner && (
                <Pressable
                  onPress={() => setInviteSheetVisible(true)}
                  className="flex-row items-center py-3 mt-1"
                  accessibilityRole="button"
                  accessibilityLabel="Invite a friend"
                >
                  <View className="w-10 h-10 rounded-full border border-dashed border-primary items-center justify-center">
                    <Ionicons name="person-add-outline" size={18} className="text-primary" />
                  </View>
                  <Text className="ml-3 text-base font-medium text-primary">
                    Invite a friend
                  </Text>
                </Pressable>
              )}
            </ScrollView>
          )}
        </View>
      </Modal>

      <InviteFriendSheet
        listId={listId}
        visible={inviteSheetVisible}
        onClose={() => setInviteSheetVisible(false)}
      />
    </>
  );
}
